// Weapons (A1, monorail-g79 — plan amoeba-environment-and-drops.md). The
// equipped weapon defines what LMB does: melee = a corridor lash from the
// membrane (snag-eats small prey — closing distance pays in food), ranged =
// a projectile toward the cursor (damage at range, no food). 'pseudopod' is
// the innate bare-hands fallback — every amoeba can lash from minute zero.
//
// A1 ships these as hardcoded def rows (species.ts-style: numbers live in
// the row). A2 (monorail-yqv) wraps them in item instances with affixes and
// generated names; these defs become the base-item layer.

import type { Entity } from './components'

export type WeaponId = 'pseudopod' | 'bristle-whip' | 'spit-gland'
export type WeaponKind = 'melee' | 'ranged'

export interface WeaponDef {
  id: WeaponId
  /** Placeholder name — final copy via amoeba-voice with the A2 name generator. */
  name: string
  /** Placeholder flavor — final copy via amoeba-voice. */
  flavor: string
  kind: WeaponKind
  damage: number
  /** Seconds between attacks. Boosts scale damage/reach, never cooldown. */
  cooldown: number
  /** Melee: lash length (world units) and corridor half-width. */
  length?: number
  width?: number
  /** Ranged: projectile speed (units/s), body radius, and flight time (s). */
  projectileSpeed?: number
  projectileRadius?: number
  ttl?: number
  tint: [number, number, number]
}

export const WEAPON_DEFS: Record<WeaponId, WeaponDef> = {
  // Innate. Deliberately a notch under the old Axopod L1 lash (0.55/0.5/0.9)
  // so any found weapon reads as an upgrade without the bare verb feeling dead.
  'pseudopod': {
    id: 'pseudopod',
    name: 'Pseudopod',
    flavor: 'Standard-issue. The body politely disagrees with things.',
    kind: 'melee',
    damage: 0.4,
    cooldown: 1.0,
    length: 0.5,
    width: 0.12,
    tint: [1.0, 0.55, 0.4],
  },
  'bristle-whip': {
    id: 'bristle-whip',
    name: 'Bristle Whip',
    flavor: 'A diatom’s spine, repurposed without asking.',
    kind: 'melee',
    damage: 0.7,
    cooldown: 1.1,
    length: 0.75,
    width: 0.15,
    tint: [0.95, 0.8, 0.45],
  },
  'spit-gland': {
    id: 'spit-gland',
    name: 'Spit Gland',
    flavor: 'Ranged opinion delivery. Mind the recoil.',
    kind: 'ranged',
    damage: 0.5,
    cooldown: 1.4,
    projectileSpeed: 2.4,
    projectileRadius: 0.045,
    ttl: 1.1,
    tint: [0.55, 0.9, 0.7],
  },
}

export const ALL_WEAPON_IDS = Object.keys(WEAPON_DEFS) as WeaponId[]

/** The def driving LMB right now — resolves the equipped item instance
 * (A2: Entity.equipped holds instance ids) to its base; innate pseudopod
 * when nothing is equipped. Inlined lookup rather than items.ts helpers to
 * avoid a weapons↔items import cycle. */
export function equippedWeaponDef(player: Entity): WeaponDef {
  const id = player.equipped?.weapon
  const instance = id ? player.inventory?.find(i => i.id === id) : undefined
  const base = instance?.base as WeaponId | undefined
  return (base && WEAPON_DEFS[base]) || WEAPON_DEFS['pseudopod']
}
